import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Payment {
  id: string;
  project_id: string;
  client_id: string;
  freelancer_id?: string;
  amount: number;
  status: 'pending' | 'paid' | 'released' | 'refunded' | 'cancelled';
  payment_method?: string;
  payment_reference?: string;
  payment_date?: string;
  released_at?: string;
  refunded_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Project {
  client_id: string;
  id: string;
  title: string;
  budget: number;
  status: string;
  payment_status: string;
  client_name: string;
  freelancer_name?: string;
  deadline: string;
  created_at: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  is_approved: boolean;
}

const PaymentSimulation: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [paymentMethod, setPaymentMethod] = useState<string>('simulated');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);

        // Load project details
        const projectResponse = await axios.get(
          `http://localhost:5000/api/projects/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (projectResponse.data.success) {
          setProject(projectResponse.data.data.project);
        }

        // Load payment details
        const paymentResponse = await axios.get(
          `http://localhost:5000/api/projects/${projectId}/payment`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (paymentResponse.data.success) {
          setPayment(paymentResponse.data.data.payment);
        }

      } catch (error: any) {
        console.error('Error loading payment data:', error);
        showMessage('Error loading payment information', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [projectId, navigate]);

  const showMessage = (msg: string, type: 'success' | 'error' | 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleSimulatePayment = async () => {
    if (!project || !user) return;

    setIsProcessing(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/pay`,
        {
          payment_method: paymentMethod,
          payment_reference: `SIM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        showMessage('Payment simulated successfully!', 'success');
        setPayment(response.data.data.project.payment);
        setProject(response.data.data.project);
        
        // Refresh payment data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Payment simulation error:', error);
      showMessage(
        error.response?.data?.message || 'Error processing payment simulation',
        'error'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRequestRefund = async () => {
    if (!refundReason.trim()) {
      showMessage('Please provide a reason for the refund', 'error');
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/refund`,
        {
          reason: refundReason,
          payment_method: paymentMethod
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        showMessage('Refund request submitted successfully!', 'success');
        setShowRefundModal(false);
        setRefundReason('');
        
        // Refresh payment data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Refund request error:', error);
      showMessage(
        error.response?.data?.message || 'Error submitting refund request',
        'error'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'released':
        return 'bg-blue-100 text-blue-800';
      case 'refunded':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Pending';
      case 'released':
        return 'Released to Freelancer';
      case 'refunded':
        return 'Refunded';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (!user || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="warning-message max-w-md">
            <h2 className="text-xl font-bold text-yellow-800 mb-4">Project Not Found</h2>
            <p className="text-yellow-700 mb-6">
              The project you're trying to access doesn't exist or you don't have permission to view it.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== 'client' || user.id !== project.client_id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="error-message max-w-md">
            <h2 className="text-xl font-bold text-red-800 mb-4">Access Denied</h2>
            <p className="text-red-700 mb-6">
              Only the project client can access payment information.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="flex items-center gap-">
            <div className="navbar-brand">
              <div className="brand-logo">NS</div>
              <span className="brand-text">NexSync</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button 
                type="button"
                onClick={() => navigate('/dashboard')}
                className="nav-link"
                title="Dashboard"
              >
                Dashboard
              </button>
              <button 
                type="button"
                onClick={() => navigate('/client/projects')}
                className="nav-link nav-link-active"
                title="My Projects"
              >
                My Projects
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* User Info */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                <div className="flex items-center gap-2">
                  <span className="role-badge role-client">Client</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="payment-simulation-container">
        <div className="payment-simulation-content">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Payment Simulation
                </h1>
                <p className="text-gray-600">
                  Project: <span className="font-semibold">{project.title}</span>
                </p>
              </div>
              <button 
                type="button"
                onClick={() => navigate(`/client/projects/${projectId}`)} 
                className="btn btn-secondary"
                title="Back to Project"
              >
                ← Back to Project
              </button>
            </div>
          </div>

          {message && (
            <div className={`message ${
              messageType === 'success' ? 'success-message' :
              messageType === 'error' ? 'error-message' :
              'info-message'
            } mb-6`}>
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Payment Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Payment Summary Card */}
              <div className="payment-card">
                <h2 className="card-title mb-6">Payment Summary</h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Project Amount:</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {formatCurrency(project.budget)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Platform Fee (5%):</span>
                    <span className="text-lg font-medium text-gray-700">
                      {formatCurrency(project.budget * 0.05)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Freelancer Earnings:</span>
                    <span className="text-lg font-medium text-green-600">
                      {formatCurrency(project.budget * 0.95)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-3">
                    <span className="text-lg font-semibold text-gray-900">Total to Pay:</span>
                    <span className="text-3xl font-bold text-green-600">
                      {formatCurrency(project.budget)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Status Card */}
              <div className="payment-card">
                <h2 className="card-title mb-6">Payment Status</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">Current Status</h3>
                      <p className="text-sm text-gray-600">
                        {payment ? `Payment ID: ${payment.id}` : 'No payment record found'}
                      </p>
                    </div>
                    {payment && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(payment.status)}`}>
                        {getPaymentStatusText(payment.status)}
                      </span>
                    )}
                  </div>

                  {payment && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Payment Method</p>
                          <p className="font-medium">{payment.payment_method || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Reference</p>
                          <p className="font-medium font-mono text-sm">{payment.payment_reference || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Payment Date</p>
                          <p className="font-medium">{formatDate(payment.payment_date)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Released Date</p>
                          <p className="font-medium">{formatDate(payment.released_at)}</p>
                        </div>
                      </div>

                      {payment.notes && (
                        <div>
                          <p className="text-sm text-gray-600">Notes</p>
                          <p className="font-medium text-gray-700 bg-gray-50 p-3 rounded mt-1">
                            {payment.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Project Information Card */}
              <div className="payment-card">
                <h2 className="card-title mb-6">Project Information</h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Project ID</p>
                      <p className="font-medium font-mono">{project.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Project Status</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        project.status === 'completed' ? 'bg-green-100 text-green-800' :
                        project.status === 'active' ? 'bg-blue-100 text-blue-800' :
                        project.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600">Client</p>
                    <p className="font-medium">{project.client_name}</p>
                  </div>

                  {project.freelancer_name && (
                    <div>
                      <p className="text-sm text-gray-600">Freelancer</p>
                      <p className="font-medium">{project.freelancer_name}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Deadline</p>
                      <p className="font-medium">{formatDate(project.deadline)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Created</p>
                      <p className="font-medium">{formatDate(project.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Payment Actions */}
            <div className="space-y-6">
              {/* Simulated Payment Card */}
              <div className="payment-card">
                <h2 className="card-title mb-6">Simulate Payment</h2>
                
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-blue-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-1">Development Mode</h4>
                        <p className="text-sm text-blue-700">
                          This is a simulated payment for development purposes. No real money will be transferred.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="paymentMethod" className="form-label">
                      Payment Method
                    </label>
                    <select
                      id="paymentMethod"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="form-input"
                      disabled={payment?.status === 'paid' || payment?.status === 'released'}
                    >
                      <option value="simulated">Simulated Payment</option>
                      <option value="bank_transfer">Bank Transfer (Simulated)</option>
                      <option value="card">Card Payment (Simulated)</option>
                      <option value="wallet">Digital Wallet (Simulated)</option>
                    </select>
                  </div>

                  {/* Payment Actions */}
                  <div className="space-y-3">
                    {(!payment || payment.status === 'pending') && (
                      <button
                        onClick={handleSimulatePayment}
                        disabled={isProcessing}
                        className="btn btn-primary w-full"
                      >
                        {isProcessing ? (
                          <>
                            <div className="loading-spinner mr-2"></div>
                            Processing Payment...
                          </>
                        ) : (
                          `Simulate Payment - ${formatCurrency(project.budget)}`
                        )}
                      </button>
                    )}

                    {payment && payment.status === 'paid' && project.status !== 'completed' && (
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-green-700">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">Payment Complete</span>
                          </div>
                          <p className="text-sm text-green-600 mt-1">
                            Your payment has been received. The freelancer has been notified to start working.
                          </p>
                        </div>

                        <button
                          onClick={() => navigate(`/client/projects/${projectId}`)}
                          className="btn btn-secondary w-full"
                        >
                          View Project Status
                        </button>
                      </div>
                    )}

                    {payment && payment.status === 'released' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-blue-700">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="font-medium">Payment Released</span>
                        </div>
                        <p className="text-sm text-blue-600 mt-1">
                          Payment has been released to the freelancer. Thank you for using NexSync!
                        </p>
                      </div>
                    )}

                    {payment && (payment.status === 'paid' || payment.status === 'pending') && (
                      <button
                        onClick={() => setShowRefundModal(true)}
                        className="btn btn-outline w-full text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Request Refund
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Security Card */}
              <div className="payment-card bg-gray-50">
                <h3 className="font-semibold text-gray-900 mb-4">Payment Security</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="text-green-600 mt-0.5">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Escrow Protection</p>
                      <p className="text-xs text-gray-600">
                        Funds are held securely until you approve the completed work.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="text-green-600 mt-0.5">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Secure Processing</p>
                      <p className="text-xs text-gray-600">
                        All transactions are processed through secure simulated channels.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="text-green-600 mt-0.5">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Money-Back Guarantee</p>
                      <p className="text-xs text-gray-600">
                        Get a full refund if the work doesn't meet your requirements.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Help Card */}
              <div className="payment-card bg-blue-50 border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-4">Need Help?</h3>
                
                <div className="space-y-3">
                  <p className="text-sm text-gray-700">
                    For questions about payments or refunds, please contact our support team.
                  </p>
                  
                  <div className="space-y-2">
                    <a
                      href="/helpcenter"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      Visit Help Center
                    </a>
                    
                    <a
                      href="/contactus"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Contact Support
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Refund</h3>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Please provide a reason for your refund request. Our team will review your request within 24-48 hours.
                </p>
                
                <div className="form-group">
                  <label htmlFor="refundReason" className="form-label">
                    Reason for Refund *
                  </label>
                  <textarea
                    id="refundReason"
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    className="form-input min-h-[120px]"
                    placeholder="Please explain why you're requesting a refund..."
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum 500 characters
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRefundModal(false);
                    setRefundReason('');
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestRefund}
                  disabled={isProcessing || !refundReason.trim()}
                  className="btn btn-primary flex-1 bg-red-600 hover:bg-red-700"
                >
                  {isProcessing ? (
                    <>
                      <div className="loading-spinner mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Refund Request'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              <strong>Important:</strong> This is a simulated payment system for development purposes only.
              No real financial transactions are processed through this platform.
            </p>
            <div className="mt-4 flex justify-center space-x-6">
              <a href="/termsofservice" className="text-sm text-gray-500 hover:text-gray-900">
                Terms of Service
              </a>
              <a href="/privacypolicy" className="text-sm text-gray-500 hover:text-gray-900">
                Privacy Policy
              </a>
              <a href="/cookiepolicy" className="text-sm text-gray-500 hover:text-gray-900">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaymentSimulation;